import React, { useState } from "react";
import { toast } from "react-toastify";
import { submitContactForm } from "../../utils/api";
import { validateContactForm } from "../../utils/validate";
import ThankYouModal from "../ThankYouModal";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    services: [],
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const inputClass = "p-2 rounded bg-slate-800 focus:outline-none w-full";
  const errorClass = "text-red-500 text-sm mt-1";

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      if (!/^\d*$/.test(value) || value.length > 10) return;

      setFormData((prev) => ({
        ...prev,
        phone: value,
      }));

      setFormErrors((prev) => ({
        ...prev,
        phone:
          value.length > 0 && value.length < 10
            ? "Phone number must be exactly 10 digits"
            : "",
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      services: checked
        ? [...prev.services, value]
        : prev.services.filter((service) => service !== value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateContactForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = {
        ...formData,
        services: JSON.stringify(formData.services),
      };

      const result = await submitContactForm(formDataToSend);

      if (result.success) {
        setIsModalOpen(true);
      } else {
        toast.error(result.message || "Something went wrong. Please try again.");
      }

      resetForm();
    } catch (error) {
      toast.error(error.message || "An error occurred. Please try again.");
      resetForm();
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      services: [],
      message: "",
    });
  };

  return (
    <div className="py-6">
      <div className="grid grid-cols-1 gap-24">
        <form
          onSubmit={handleSubmit}
          className="bg-slate-900 text-white p-12 rounded-xl space-y-6"
        >
          <h3 className="text-3xl font-bold">Get in Touch With Us</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
            <div>
              <input
                type="text"
                name="first_name"
                placeholder="First Name*"
                value={formData.first_name}
                onChange={handleChange}
                className={inputClass}
              />
              {formErrors.first_name && (
                <p className={errorClass}>{formErrors.first_name}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                name="last_name"
                placeholder="Last Name*"
                value={formData.last_name}
                onChange={handleChange}
                className={inputClass}
              />
              {formErrors.last_name && (
                <p className={errorClass}>{formErrors.last_name}</p>
              )}
            </div>
            <div>
              <input
                type="email"
                name="email"
                placeholder="E-mail*"
                value={formData.email}
                onChange={handleChange}
                className={inputClass}
              />
              {formErrors.email && (
                <p className={errorClass}>{formErrors.email}</p>
              )}
            </div>
            <div>
              <input
                type="number"
                name="phone"
                placeholder="Phone"
                value={formData.phone}
                onChange={handleChange}
                className={inputClass}
              />
              {formErrors.phone && (
                <p className={errorClass}>{formErrors.phone}</p>
              )}
            </div>
          </div>

          {/* Services dropdown heading */}
          <div>
            <button
              type="button"
              onClick={() => setServicesOpen((prev) => !prev)}
              className="flex items-center justify-between w-full bg-slate-800 p-3 rounded cursor-pointer text-white font-semibold"
              aria-expanded={servicesOpen}
              aria-controls="services-checkboxes"
            >
              <div className="font-medium">What service are you looking for?</div>
              <svg
                className={`w-5 h-5 transform transition-transform ${
                  servicesOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>

            {servicesOpen && (
              <div
                id="services-checkboxes"
                className="mt-3 space-y-2"
              >
                {[
                  { label: "Web Development", value: "web_development" },
                  { label: "App Development", value: "app_development" },
                  { label: "Staffing Solutions", value: "staffing_solutions" },
                  { label: "SMS & WhatsApp API Solutions", value: "sms_whatsapp_api" },
                  { label: "Digital Marketing", value: "digital_marketing" },
                ].map((service) => (
                  <label
                    key={service.value}
                    className="flex items-center space-x-2 text-white"
                  >
                    <input
                      type="checkbox"
                      value={service.value}
                      checked={formData.services.includes(service.value)}
                      onChange={handleCheckboxChange}
                      className="accent-blue-500"
                    />
                    <span>{service.label}</span>
                  </label>
                ))}
              </div>
            )}
            {formErrors.services && (
              <p className={errorClass}>{formErrors.services}</p>
            )}
          </div>

          <div>
            <textarea
              name="message"
              rows="4"
              placeholder="Type Your Message here"
              value={formData.message}
              onChange={handleChange}
              className="w-full p-2 rounded bg-slate-800 focus:outline-none"
            />
            {formErrors.message && (
              <p className={errorClass}>{formErrors.message}</p>
            )}
          </div>
          <button
            type="submit"
            className="w-full bg-sky-200 text-black font-semibold py-2 rounded hover:bg-sky-300 transition"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
          <ThankYouModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
